import React from 'react';
import styled from 'styled-components';

import { Text } from '@latitude/text';
import { Box } from '@latitude/box';

import { formatAsCurrency } from '../../utils';
import {
  ALIGN,
  LOAN_PAY_FREQUENCY,
  PL_PPSR_SEARCH_FEE,
  PL_PPSR_REGISTRATION_FEE,
  FONT_WEIGHT,
  BUTTON_THEME,
  FONT_SIZE,
  CALCULATOR_TRACKING_V2
} from '../../utils/constants';
import Modal from '../ModalRebrand/ModalRebrand';
import Typography from '@latitude/typography';
import { REBRAND_COLOR } from '@latitude/core/utils/constants';
import Button from '@latitude/button-rebrand';

const REPAYMENT_FREQ_TEXT = {
  [LOAN_PAY_FREQUENCY.WEEKLY]: 'week',
  [LOAN_PAY_FREQUENCY.FORTNIGHTLY]: 'fortnight',
  [LOAN_PAY_FREQUENCY.MONTHLY]: 'month'
};

const RatesFeesTable = styled.table`
  && {
    width: 100%;
  }
  && tr {
    vertical-align: top;
  }
  && tr th {
    padding: 5px 10px;
    font-family: Roboto;
    font-size: 14px;
    font-weight: 300;
  }
  && tr td {
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    padding: 5px 10px;
    text-align: right;
  }
`;

function FeeBreakDownModal({
  showFeesModal,
  setShowFeesModal,
  repaymentAmount,
  repaymentPeriod,
  isVariable,
  isSecured,
  isLefWaiver
}) {
  function formatPeriod(repaymentPeriod) {
    const result = REPAYMENT_FREQ_TEXT[repaymentPeriod];
    return result ? result : '(unknown)';
  }
  function calcLoanServiceFee(amountPerMonth, repaymentPeriod) {
    if (repaymentPeriod === LOAN_PAY_FREQUENCY.WEEKLY) {
      return amountPerMonth / 4.0;
    } else if (repaymentPeriod === LOAN_PAY_FREQUENCY.FORTNIGHTLY) {
      return amountPerMonth / 2.0;
    } else if (repaymentPeriod === LOAN_PAY_FREQUENCY.MONTHLY) {
      return amountPerMonth;
    } else {
      return -1;
    }
  }

  const repaymentPeriodText = formatPeriod(repaymentPeriod);
  const loanServiceFee = calcLoanServiceFee(16.5, repaymentPeriod);

  return (
    <Modal
      isOpen={showFeesModal}
      onRequestClose={() => {
        return setShowFeesModal(false);
      }}
      width="fit-content"
      css={`
        &&.modal__panel {
          padding: 32px;
        }
        && div.modal__content {
          padding-bottom: 0 !important;
        }
      `}
      title={
        <Typography.SH4 semibold color={REBRAND_COLOR.BLACK}>
          Fee breakdown
        </Typography.SH4>
      }
      content={
        <Box>
          <div>
            <Typography.Body color={REBRAND_COLOR.BLACK}>
              Estimated repayments
            </Typography.Body>
            <div css={{ display: 'flex', alignItems: 'flex-end' }}>
              <Typography.N4 color={REBRAND_COLOR.MID_BLUE}>
                {formatAsCurrency(repaymentAmount)}
              </Typography.N4>
              <div css={{ paddingBottom: '5px' }}>
                <Typography.SH3 color={REBRAND_COLOR.BLACK}>
                  {' / '} {repaymentPeriodText}
                </Typography.SH3>
              </div>
            </div>
            <hr css={{ margin: '0 0 12px 0', color: REBRAND_COLOR.STONE }} />
          </div>
          <Typography.Body semibold color={REBRAND_COLOR.BLACK}>
            Standard payments
          </Typography.Body>
          <RatesFeesTable
            css={`
              text-align: left;
            `}
          >
            <tr>
              <th css={{ paddingLeft: '0px !important' }}>
                <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                  Standard payment including
                  <br />
                  interest and establishment fee (
                  {isLefWaiver ? '$0 originally $395' : '$395'})
                </Typography.Body>
              </th>
              <td>
                <Typography.Body color={REBRAND_COLOR.BLACK}>
                  {formatAsCurrency(repaymentAmount - loanServiceFee, 2)}
                  {' / '}
                  {repaymentPeriodText}
                </Typography.Body>
              </td>
            </tr>
            <tr>
              <th css={{ paddingLeft: '0px !important' }}>
                <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                  Loan Service Fee ($16.50 / month)
                </Typography.Body>
              </th>
              <td>
                <Typography.Body color={REBRAND_COLOR.BLACK}>
                  {formatAsCurrency(loanServiceFee, 2)}
                  {' / '}
                  {repaymentPeriodText}
                </Typography.Body>
              </td>
            </tr>
            <tr>
              <th css={{ paddingLeft: '0px !important' }}>
                <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                  Direct Debit Fee
                </Typography.Body>
              </th>
              <td>
                <Typography.Body color={REBRAND_COLOR.BLACK}>
                  {'$0 / '}
                  {repaymentPeriodText}
                </Typography.Body>
              </td>
            </tr>
            {isSecured && (
              <>
                <tr>
                  <th css={{ paddingLeft: '0px !important' }}>
                    <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                      PPSR Search Fee
                    </Typography.Body>
                  </th>
                  <td>
                    <Typography.Body color={REBRAND_COLOR.BLACK}>
                      {`$${PL_PPSR_SEARCH_FEE}.00`}
                    </Typography.Body>
                  </td>
                </tr>

                <tr>
                  <th css={{ paddingLeft: '0px !important' }}>
                    <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                      PPSR Registration Fee
                    </Typography.Body>
                  </th>
                  <td>
                    <Typography.Body color={REBRAND_COLOR.BLACK}>
                      {`$${PL_PPSR_REGISTRATION_FEE}.00`}
                    </Typography.Body>
                  </td>
                </tr>
              </>
            )}
          </RatesFeesTable>
          <hr css={{ margin: '0 0 12px 0', color: REBRAND_COLOR.STONE }} />
          <Typography.Body semibold color={REBRAND_COLOR.BLACK}>
            Other fees (if applicable)
          </Typography.Body>
          <RatesFeesTable
            css={`
              text-align: left;
            `}
          >
            <tr css={{ paddingLeft: '0px !important' }}>
              <th css={{ paddingLeft: '0px !important' }}>
                <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                  BPAY® payment handling fee
                </Typography.Body>
              </th>
              <td>
                <Typography.Body color={REBRAND_COLOR.BLACK}>
                  $1.95
                </Typography.Body>
              </td>
            </tr>
            <tr>
              <th css={{ paddingLeft: '0px !important' }}>
                <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                  Late payment fee
                </Typography.Body>
              </th>
              <td>
                <Typography.Body color={REBRAND_COLOR.BLACK}>
                  $45
                </Typography.Body>
              </td>
            </tr>

            <tr>
              <th css={{ paddingLeft: '0px !important' }}>
                <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                  Early termination fee
                </Typography.Body>
              </th>
              <td>
                <Typography.Body color={REBRAND_COLOR.BLACK}>
                  {isVariable
                    ? '$0'
                    : '$500 (up until the final 3 months of loan term)'}
                </Typography.Body>
              </td>
            </tr>
          </RatesFeesTable>
          <Button
            variant="primary"
            onClick={() => setShowFeesModal(false)}
            size="lg"
            ctaText="Close"
            targetBlank="_blank"
            style={{ width: '100%' }}
            trackEventData={{
              category: CALCULATOR_TRACKING_V2.CATEGORY,
              action: CALCULATOR_TRACKING_V2.ACTION,
              location: CALCULATOR_TRACKING_V2.LOCATION,
              label: 'Close modal view fee breakdown'
            }}
          />
        </Box>
      }
      ariaHideApp={false}
    />
  );
}

export default FeeBreakDownModal;
